/**
 * VH Staff Dashboard
 *
 * Dedicated, simple dashboard for Virtual Host staff members.
 * Completely separate from the OAuth-based RestaurantDashboard.
 *
 * Features:
 * - Simple header with restaurant name, staff name, logout
 * - VirtualHostControlCenter as the main content
 * - No menu management, analytics, or owner-only features
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User } from 'lucide-react';
import { usePageTitle } from '@/hooks/usePageTitle';
import VirtualHostControlCenter from '@/components/virtual-host/control-center/VirtualHostControlCenter';

const VHStaffDashboard = () => {
  usePageTitle('vhStaffDashboard');
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  // Get staff info from localStorage (set during login)
  const userType = localStorage.getItem('user_type');
  const staffName = localStorage.getItem('staff_name') || 'Staff';
  const restaurantName = localStorage.getItem('restaurant_name') || 'Restaurant';
  const restaurantSlug = localStorage.getItem('restaurant_slug') || '';
  const restaurantLogoUrl = localStorage.getItem('restaurant_logo_url') || '';

  // Check authentication on mount
  useEffect(() => {
    if (userType !== 'vh_staff') {
      // Not a VH Staff user, redirect to login
      setIsAuthenticated(false);
    } else {
      setIsAuthenticated(true);
    }
  }, [userType]);

  // Handle logout
  const handleLogout = async () => {
    // Clear all VH Staff localStorage data
    localStorage.removeItem('user_type');
    localStorage.removeItem('staff_name');
    localStorage.removeItem('restaurant_name');
    localStorage.removeItem('restaurant_slug');
    localStorage.removeItem('restaurant_logo_url');
    localStorage.removeItem('vh_staff_data');

    // Call backend to clear the httpOnly cookie
    try {
      await fetch('/api/vh-staff/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout API call failed:', error);
    }

    // Navigate to branded login if we have slug, otherwise generic login
    if (restaurantSlug) {
      navigate(`/staff/${restaurantSlug}`);
    } else {
      navigate('/vh-staff-login');
    }
  };

  // Show loading while checking auth
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 border-4 border-[#FDB913] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    // Use useEffect to navigate to avoid render-time navigation
    navigate('/vh-staff-login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Simple Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left: Restaurant Logo + Name */}
            <div className="flex items-center gap-3">
              {restaurantLogoUrl ? (
                <img
                  src={restaurantLogoUrl}
                  alt={restaurantName}
                  className="h-10 w-auto max-w-[120px] object-contain"
                />
              ) : (
                <div className="h-10 w-10 bg-gray-900 rounded-lg flex items-center justify-center">
                  <span className="text-[#FDB913] font-bold text-sm">AI</span>
                </div>
              )}
              <div>
                <h1 className="text-lg font-semibold text-gray-900">{restaurantName}</h1>
                <p className="text-xs text-gray-500">AI Host Control Center</p>
              </div>
            </div>

            {/* Right: Staff Info + Logout */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <User className="h-4 w-4" />
                <span>{staffName}</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content: VH Control Center */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full">
        <VirtualHostControlCenter hideHeader={true} isStaff={true} />
      </main>

      {/* Footer - Full width (outside max-w-7xl container) */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Dine AI LLC. All rights reserved.
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a
                href="/terms"
                target="_blank"
                className="text-gray-500 hover:text-blue-600 transition-colors"
              >
                Terms of Service
              </a>
              <a
                href="/privacy"
                target="_blank"
                className="text-gray-500 hover:text-blue-600 transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="/cookies"
                target="_blank"
                className="text-gray-500 hover:text-blue-600 transition-colors"
              >
                Cookie Policy
              </a>
              <a
                href="/accessibility"
                target="_blank"
                className="text-gray-500 hover:text-blue-600 transition-colors"
              >
                Accessibility
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VHStaffDashboard;
